
import React, { useState } from 'react';
import { Role } from '../types';

type GuideSection = {
    title: string;
    icon: string;
    steps: string[];
};

const GUIDES: Record<Role, GuideSection[]> = {
    principal: [
        {
            title: "Getting Started",
            icon: "flag",
            steps: [
                "**Create an Academic Year:** Go to 'Class Management' and create a year (e.g., '2024-2025'). This acts as the container for all classes.",
                "**Create Classes:** Inside an Academic Year, add classes (e.g., 'Grade 5A', 'Math 101').",
                "**Invite Staff:** Go to 'Manage Staff'. You can either invite teachers directly by email or generate codes for them to join.",
                "**Assign Teachers:** Once teachers join, open a Class and click 'Assign Teachers' to link them."
            ]
        },
        {
            title: "Communication",
            icon: "campaign",
            steps: [
                "**Broadcasts:** Use the 'Broadcast' button to send announcements to the whole school or specific classes. You can attach images.",
                "**Staff Chat:** Use the dedicated channel to communicate securely with all approved teachers.",
                "**Student Lookup:** Search for any student in the search bar to view their profile and parent chat history."
            ]
        },
        {
            title: "School Culture",
            icon: "military_tech",
            steps: [
                "**Kudos Log:** Monitor positive reinforcement across the school.",
                "**Principal's Award:** Send a special, high-value badge to students or teachers to recognize exceptional effort.",
                "**Calendar:** Manage school-wide events like holidays and exams."
            ]
        }
    ],
    teacher: [
        {
            title: "Daily Tasks",
            icon: "check_circle",
            steps: [
                "**Take Attendance:** Click the 'Attendance' button on a class card. Mark students Present, Late, or Absent.",
                "**Assign Homework:** Go to the 'Homework' tab. You can type or **speak** the assignment. Use 'Process with AI' to automatically organize raw text into structured tasks.",
                "**Calendar:** Add class-specific events like 'Field Trip' or 'Quiz' that parents can see."
            ]
        },
        {
            title: "Communication",
            icon: "chat",
            steps: [
                "**Parent Chat:** Chat individually with parents. Messages are **automatically translated** between your language and theirs.",
                "**Voice Notes:** You can record audio messages for quick updates.",
                "**Broadcasts:** Principals may allow you to send announcements to your specific classes."
            ]
        },
        {
            title: "Data & Insights",
            icon: "insights",
            steps: [
                "**Weekly Insights:** In the 'Insights' tab, click 'Generate' to get an AI analysis of attendance, homework, and parent logs.",
                "**Class Pulse:** View aggregate data on student stress, sleep, and screen time in the 'Stats' tab.",
                "**Kudos:** Send badges like 'Problem Solver' or 'Kindness Captain' to encourage students."
            ]
        }
    ],
    parent: [
        {
            title: "Connecting",
            icon: "link",
            steps: [
                "**Add Child:** Click the '+' icon in the top right. Enter the unique **Invitation Code** provided by your teacher.",
                "**Multiple Children:** You can manage multiple children from different schools in one dashboard."
            ]
        },
        {
            title: "Supporting Learning",
            icon: "school",
            steps: [
                "**Homework Hub:** View daily assignments. Check off tasks as your child completes them.",
                "**AI Tutor:** If your child is stuck, click **'I'm Stuck?'** on a task. Our AI Tutor provides hints and examples (it never gives the direct answer) to help them learn.",
                "**Resources:** The AI Tutor can also find safe, external videos or articles for difficult topics."
            ]
        },
        {
            title: "Wellbeing & Communication",
            icon: "favorite",
            steps: [
                "**Weekly Check-in:** Every week, submit a quick update on your child's sleep, stress, and mood. This helps teachers support them better.",
                "**Chat:** Message teachers in **your native language**. The app translates it for the teacher instantly.",
                "**Family Connect:** Use the 'Family Connect' card to get daily, fun conversation starters to build character and resilience at home."
            ]
        }
    ]
};

export const HelpModal = ({ role, onClose }: { role: Role, onClose: () => void }) => {
    const sections = GUIDES[role];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="bg-indigo-600 p-6 text-white flex justify-between items-start">
                    <div>
                        <h2 className="text-2xl font-bold flex items-center gap-2">
                            <span className="material-icons-round">help_outline</span>
                            How to use EduGravity
                        </h2>
                        <p className="opacity-80 text-sm mt-1">Quick guide for {role.charAt(0).toUpperCase() + role.slice(1)}s</p>
                    </div>
                    <button onClick={onClose} className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors">
                        <span className="material-icons-round">close</span>
                    </button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-6 space-y-8">
                    {sections.map((section, idx) => (
                        <div key={idx} className="space-y-4">
                            <div className="flex items-center gap-3 text-indigo-700 border-b pb-2 border-indigo-100">
                                <span className="material-icons-round text-2xl">{section.icon}</span>
                                <h3 className="text-xl font-bold">{section.title}</h3>
                            </div>
                            <ul className="space-y-4 pl-2">
                                {section.steps.map((step, sIdx) => {
                                    // Parse bold markdown-like syntax **text**
                                    const parts = step.split(/(\*\*.*?\*\*)/g);
                                    return (
                                        <li key={sIdx} className="flex gap-3 text-gray-700 text-sm leading-relaxed">
                                            <span className="w-6 h-6 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5">
                                                {sIdx + 1}
                                            </span>
                                            <span>
                                                {parts.map((part, pIdx) => {
                                                    if (part.startsWith('**') && part.endsWith('**')) {
                                                        return <strong key={pIdx} className="text-gray-900">{part.slice(2, -2)}</strong>;
                                                    }
                                                    return part;
                                                })}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </div>
                
                <div className="p-4 bg-gray-50 border-t text-center text-xs text-gray-500">
                    Need more help? Contact your school administrator.
                </div>
            </div>
        </div>
    );
};
